/* Multi-file selection state for the explorer tree.
 *
 * Public API:
 *   getSelected(): string[]
 *   isSelected(path): boolean
 *   size(): number
 *   getAnchor(): string | null
 *   select(path, mode)     // mode: "replace" | "toggle" | "range"
 *   selectSiblingsOfAnchor()
 *   clear()
 *   prune(validPaths: Set<string>)
 *   onChange(cb): unsubscribe
 *   setVisiblePathsProvider(fn)
 *
 * "range" relies on a visible-paths provider so the module can compute the
 * slice between anchor and target across the tree's current row order.
 */

const _selected = new Set();
let _anchor = null;
const _listeners = new Set();
let _visiblePathsProvider = () => [];

function _emit() { for (const cb of _listeners) cb(); }

export function setVisiblePathsProvider(fn) { _visiblePathsProvider = fn; }

export function getSelected() { return [..._selected]; }
export function isSelected(path) { return _selected.has(path); }
export function size() { return _selected.size; }
export function getAnchor() { return _anchor; }

export function select(path, mode = "replace") {
  if (!path) return;
  if (mode === "replace") {
    _selected.clear();
    _selected.add(path);
    _anchor = path;
  } else if (mode === "toggle") {
    if (_selected.has(path)) _selected.delete(path);
    else _selected.add(path);
    _anchor = path;
  } else if (mode === "range") {
    const visible = _visiblePathsProvider();
    if (!_anchor || !visible.length) {
      _selected.clear();
      _selected.add(path);
      _anchor = path;
    } else {
      const i = visible.indexOf(_anchor);
      const j = visible.indexOf(path);
      if (i < 0 || j < 0) {
        _selected.clear();
        _selected.add(path);
        _anchor = path;
      } else {
        const [lo, hi] = i <= j ? [i, j] : [j, i];
        _selected.clear();
        for (let k = lo; k <= hi; k++) _selected.add(visible[k]);
      }
    }
  }
  _emit();
}

export function selectSiblingsOfAnchor() {
  if (!_anchor) return;
  const visible = _visiblePathsProvider();
  const parts = _anchor.split('/');
  const parentPrefix = parts.slice(0, -1).join('/') + '/';
  const siblings = visible.filter(p => {
    if (!p.startsWith(parentPrefix)) return false;
    const tail = p.slice(parentPrefix.length);
    return tail.length > 0 && !tail.includes('/');
  });
  if (!siblings.length) return;
  _selected.clear();
  for (const p of siblings) _selected.add(p);
  _emit();
}

export function clear() {
  if (!_selected.size && _anchor === null) return;
  _selected.clear();
  _anchor = null;
  _emit();
}

export function prune(validPaths) {
  let changed = false;
  for (const p of [..._selected]) {
    if (!validPaths.has(p)) { _selected.delete(p); changed = true; }
  }
  if (_anchor && !validPaths.has(_anchor)) { _anchor = null; changed = true; }
  if (changed) _emit();
}

export function onChange(cb) {
  _listeners.add(cb);
  return () => _listeners.delete(cb);
}
